import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { CA, caErr } from "@/lib/clientAdmin";

// Ids of clients needing attention: server health list when available, else client-side stats.
export const riskyIds = (h, clients) => new Set(h?.data?.items ? h.data.items.map((x) => x.client_id) : clients.filter((c) => healthIssues(c, h?.data?.days || 14).length).map((c) => c.id));

const DAY = 86400000;
const since = (d) => (d ? Math.floor((Date.now() - new Date(d)) / DAY) : null);
// Client-side fallback: no sign-in or no workflow run for 14+ days.
export const healthIssues = (c, days = 14) => {
  if (c.status === "paused") return [];
  const s = c.stats || {}; const out = [];
  const si = since(s.last_sign_in_at); const wr = since(s.last_workflow_run_at);
  if (si == null || si >= days) out.push(si == null ? "Never signed in" : `No sign-in for ${si} days`);
  if ((c.modules?.radar || []).length && (wr == null || wr >= days)) out.push(wr == null ? "No workflow runs yet" : `No workflow run for ${wr} days`);
  return out;
};

// §80 "Needs attention" banner on Client Admin + email-alert toggle.
export const HealthAlerts = ({ clients, onOpen, h }) => {
  const [email, setEmail] = useState(null);
  useEffect(() => { if (h.data) setEmail(h.data.email_enabled ?? true); }, [h.data]);
  const days = h.data?.days || 14;
  const list = h.data?.items ? h.data.items.map((x) => ({ id: x.client_id, company: x.company, issues: x.issues || [] }))
    : clients.map((c) => ({ id: c.id, company: c.company, issues: healthIssues(c, days) })).filter((x) => x.issues.length);
  const toggle = async (v) => { setEmail(v); try { await api.put(`${CA}/health/settings`, { email_enabled: v, days }); toast.success(v ? "You'll get an email when a client goes quiet" : "Health alert emails off"); } catch (e) { setEmail(!v); toast.error(caErr(e)); } };
  if (!list.length && !h.data) return null;
  return (
    <div className={`rounded-2xl border p-4 ${list.length ? "border-amber-500/40 bg-amber-500/5" : "border-[#27272A]"}`} data-testid="ca-health">
      <div className="flex flex-wrap items-center gap-3"><AlertTriangle className={`h-4 w-4 ${list.length ? "text-amber-300" : "text-zinc-600"}`} />
        <p className="flex-1 text-sm text-zinc-100" data-testid="ca-health-title">{list.length ? `${list.length} client${list.length > 1 ? "s" : ""} need${list.length > 1 ? "" : "s"} attention` : "All clients active in the last 2 weeks"}</p>
        {email !== null && <label className="flex items-center gap-2 text-[11px] text-zinc-400">Email me alerts<Switch checked={email} onCheckedChange={toggle} data-testid="ca-health-email" /></label>}</div>
      {list.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{list.map((x) => <button key={x.id} onClick={() => onOpen(x.id)} className="rounded-xl border border-amber-500/30 px-3 py-1.5 text-left text-xs hover:border-amber-400" data-testid={`ca-health-${x.id}`}><span className="block font-medium text-zinc-100">{x.company}</span><span className="block text-[11px] text-amber-200">{x.issues.join(" · ")}</span></button>)}</div>}
    </div>
  );
};
